import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { fetchNotifications, markNotificationsRead } from '../lib/api';

const rel = (s) => {
  const d = (Date.now() - new Date(s)) / 1000;
  if (d < 60) return 'now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
};

const VERB = { assigned: 'assigned you to', comment: 'commented on', mention: 'mentioned you in' };

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const d = await fetchNotifications();
      setItems(d.results || []);
      setUnread(d.unread || 0);
    } catch { /* offline / not signed in */ }
  };

  useEffect(() => {
    load();
    const onFocus = () => load();
    const id = setInterval(load, 60000);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const openTicket = async (n) => {
    setOpen(false);
    navigate(`/tickets/${n.ticket_id}`);
    if (!n.read) { await markNotificationsRead([n.id]); load(); }
  };

  const markAll = async () => {
    await markNotificationsRead();
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md border border-border p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-popover shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-medium">notifications</span>
            {unread > 0 ? <button onClick={markAll} className="text-xs text-primary hover:underline">mark all read</button> : null}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">You’re all caught up.</p>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => openTicket(n)}
                className={`flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left text-sm transition hover:bg-accent ${!n.read ? 'bg-primary/5' : ''}`}
              >
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${!n.read ? 'bg-primary' : 'bg-transparent'}`} />
                <span className="min-w-0 flex-1 leading-snug">
                  <span className="font-medium text-foreground">{n.actor?.name || 'Someone'}</span>
                  <span className="text-foreground"> {VERB[n.kind] || 'updated'} </span>
                  <span className="text-muted-foreground">#{n.ticket_id} {n.ticket_title}</span>
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{rel(n.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
